import FeatureLandingPage from "@/components/features/FeatureLandingPage";

const SITE = "https://shattaraai.com";

const howItWorks = [
  { title: "Upload Your Materials", description: "Drop in PDFs, lecture slides, YouTube videos, or voice notes. Shattara processes them instantly." },
  { title: "Ask Anything", description: "Type questions in your own words. The AI tutor explains concepts step by step, adapting to your level." },
  { title: "Get Visual Breakdowns", description: "Complex topics are broken into diagrams, analogies, and simplified explanations until they click." },
  { title: "Practice & Verify", description: "The tutor generates follow-up questions to confirm you truly understand before moving on." },
];

const benefits = [
  { title: "Personalized to Your Syllabus", description: "Explanations are based on your actual course materials, not generic textbook answers." },
  { title: "Available 24/7", description: "Ask questions at midnight before an exam — Shattara never sleeps." },
  { title: "No Judgment Zone", description: "Ask the same question ten times without embarrassment. The AI tutor adapts each time." },
  { title: "Multi-Format Support", description: "Learn from PDFs, videos, audio recordings, and slides — all in one place." },
  { title: "Step-by-Step Explanations", description: "Every answer walks you through the reasoning, not just the final result." },
  { title: "Instant Responses", description: "No waiting for office hours or tutoring appointments." },
];

const faqItems = [
  { question: "What subjects does the AI tutor support?", answer: "Shattara AI works with any subject. Upload your materials and the tutor adapts to the content — whether it's calculus, biology, history, or computer science." },
  { question: "Can I upload my own lecture slides?", answer: "Yes. You can upload PDFs, PowerPoint exports, recorded lectures, YouTube links, and voice notes. The AI tutor uses your exact materials to generate explanations." },
  { question: "Is the AI tutor accurate?", answer: "Shattara grounds its answers in your uploaded materials to minimize errors. It cites sources and explains reasoning so you can verify the logic yourself." },
  { question: "How is this different from ChatGPT?", answer: "Unlike generic AI chatbots, Shattara is built specifically for students. It reads your actual course materials, generates targeted quizzes, and tracks what you still need to learn." },
  { question: "Is Shattara AI free?", answer: "Shattara offers a free plan with daily limits. Paid plans unlock unlimited conversations, more uploads, and advanced features like exam generation." },
];

const relatedFeatures = [
  { name: "AI Flashcard Generator", href: "/ai-flashcards", description: "Turn your notes into spaced-repetition flashcards automatically." },
  { name: "AI Quiz Generator", href: "/ai-quiz-generator", description: "Create practice quizzes from your course materials in seconds." },
  { name: "AI Exam Generator", href: "/ai-exam-generator", description: "Simulate full exams with timed questions and detailed scoring." },
  { name: "AI Lecture Summarizer", href: "/ai-lecture-summarizer", description: "Get concise summaries from hours of recorded lectures." },
  { name: "What Is an AI Tutor?", href: "/what-is-ai-tutor", description: "Learn how AI tutors work and how they compare to traditional tutoring." },
  { name: "Best AI Study Tools", href: "/best-ai-study-tools", description: "Compare the top AI-powered study tools for students in 2026." },
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Shattara AI Tutor",
    description: "An AI-powered tutor that explains concepts from your own course materials with step-by-step breakdowns, visual explanations, and targeted practice.",
    url: `${SITE}/ai-tutor`,
    brand: { "@type": "Organization", name: "Shattara AI" },
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/InStock" },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to Use the Shattara AI Tutor",
    description: "Learn how to get personalized explanations from your course materials using Shattara AI.",
    step: howItWorks.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.title,
      text: s.description,
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "AI Tutor for Students — Personalized Learning from Your Own Materials",
    description: "Discover how Shattara AI's tutor helps students understand complex topics with step-by-step explanations grounded in their actual course content.",
    author: { "@type": "Organization", name: "Shattara AI" },
    publisher: { "@type": "Organization", name: "Shattara AI", url: SITE },
    url: `${SITE}/ai-tutor`,
  },
];

export default function AITutorPage() {
  return (
    <FeatureLandingPage
      seoTitle="AI Tutor for Students — Personalized Learning | Shattara AI"
      seoDescription="Upload your materials and get instant, step-by-step explanations personalized to your syllabus. Shattara AI's tutor adapts to your level and helps you truly understand."
      headline="Your Personal AI Tutor — Built for Real Learning"
      subtitle="Upload your course materials. Ask anything. Get clear, step-by-step explanations until it actually clicks."
      howItWorks={howItWorks}
      benefits={benefits}
      faqItems={faqItems}
      relatedFeatures={relatedFeatures}
      structuredData={structuredData}
    />
  );
}
